import React, { useState, useEffect } from 'react';
import { 
  addCustomPurchaseableRing,
  getCustomPurchaseableRings,
  toggleCustomRingAvailability,
  RING_SETS,
  RING_SET_INFO,
  RING_RARITIES
} from '../services/profileRingService';
import ProfileRing from './ProfileRing';

function AdminStoreRingManager({ isOpen, onClose, adminUserId }) {
  const [storeRings, setStoreRings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' or 'create'
  
  // New ring creation form
  const [newRing, setNewRing] = useState({
    name: '',
    description: '',
    set: RING_SETS.CLASSIC,
    rarity: 'common',
    price: '',
    cssClass: '',
    imageUrl: '',
    borderStyle: 'solid',
    borderWidth: '3',
    borderColor: '#3B82F6',
    animationType: 'none',
    glowIntensity: '0'
  });
  const [creating, setCreating] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [previewMode, setPreviewMode] = useState('css'); // 'css' or 'image'

  useEffect(() => {
    if (!isOpen) return;

    const loadStoreRings = async () => {
      setLoading(true);
      try {
        const rings = await getCustomPurchaseableRings();
        setStoreRings(rings);
      } catch (error) {
        console.error('Error loading store rings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStoreRings();
  }, [isOpen]);

  const handleCreateRing = async (e) => {
    e.preventDefault();
    
    if (!newRing.name.trim() || !newRing.description.trim()) {
      alert('Please fill in name and description');
      return;
    }
    
    setCreating(true);
    try {
      let imageUrl = newRing.imageUrl;
      
      // Handle image upload if file is selected
      if (imageFile) {
        // Convert to base64 for storage (in production, use proper cloud storage)
        const reader = new FileReader();
        imageUrl = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(imageFile);
        });
      }
      
      const price = parseInt(newRing.price) || RING_RARITIES[newRing.rarity].price;
      
      const ringData = {
        name: newRing.name.trim(),
        description: newRing.description.trim(),
        set: newRing.set,
        rarity: newRing.rarity,
        price: price,
        cssClass: newRing.cssClass.trim() || `ring-custom-${Date.now()}`,
        imageUrl: imageUrl,
        customStyles: {
          borderStyle: newRing.borderStyle,
          borderWidth: newRing.borderWidth,
          borderColor: newRing.borderColor,
          animationType: newRing.animationType,
          glowIntensity: newRing.glowIntensity
        }
      };
      
      const ringId = await addCustomPurchaseableRing(ringData, adminUserId);
      
      if (ringId) {
        // Add to local state
        setStoreRings(prev => [...prev, { id: ringId, ...ringData }]);
        
        // Reset form
        setNewRing({
          name: '',
          description: '',
          set: RING_SETS.CLASSIC,
          rarity: 'common',
          price: '',
          cssClass: '',
          imageUrl: '',
          borderStyle: 'solid',
          borderWidth: '3',
          borderColor: '#3B82F6',
          animationType: 'none',
          glowIntensity: '0'
        });
        setImageFile(null);
        
        alert('Store ring created successfully!');
        setActiveTab('manage');
      } else {
        alert('Failed to create ring: Database operation failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error creating ring:', error);
      alert(`Failed to create ring: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleAvailability = async (ringId, currentStatus) => {
    try {
      const success = await toggleCustomRingAvailability(ringId, !currentStatus);
      if (success) {
        setStoreRings(prev => prev.map(ring => 
          ring.id === ringId 
            ? { ...ring, isActive: !currentStatus }
            : ring
        ));
      }
    } catch (error) {
      console.error('Error toggling ring availability:', error);
      alert('Failed to update ring availability');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or SVG)');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Please select an image smaller than 2MB');
      return;
    }
    
    setImageFile(file);
    setPreviewMode('image');
  };

  const generatePreviewCSS = () => {
    return `
      .${newRing.cssClass || 'ring-preview'} .profile-ring-border {
        border: ${newRing.borderWidth}px ${newRing.borderStyle} ${newRing.borderColor};
        ${newRing.animationType === 'pulse' ? 'animation: pulse-ring 2s infinite;' : ''}
        ${newRing.animationType === 'rotate' ? 'animation: rotate 3s linear infinite;' : ''}
        ${newRing.animationType === 'glow' ? `box-shadow: 0 0 ${newRing.glowIntensity}px ${newRing.borderColor};` : ''}
      }
    `;
  };

  if (!isOpen) return null;

  return (
    <div className="admin-store-ring-modal-overlay" onClick={onClose}>
      <div className="admin-store-ring-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="admin-store-ring-modal-header">
          <h2>Store Ring Manager</h2>
          <button className="admin-store-ring-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="admin-ring-tabs">
          <button 
            className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            Manage Store Rings
          </button>
          <button 
            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create New Ring
          </button>
        </div>

        {loading ? (
          <div className="admin-ring-loading">
            <div className="emoji-loader">
              <span className="emoji-item">🛍️</span>
              <span className="emoji-item">💍</span>
              <span className="emoji-item">⚡</span>
            </div>
            <p>Loading store rings...</p>
          </div>
        ) : (
          <>
            {/* Manage Store Rings Tab */}
            {activeTab === 'manage' && (
              <div className="manage-store-rings-section">
                <h3>Store Ring Collection</h3>
                
                {storeRings.length > 0 ? (
                  <div className="store-rings-grid">
                    {storeRings.map(ring => (
                      <div key={ring.id} className={`store-ring-item ${!ring.isActive ? 'inactive' : ''}`}>
                        <div className="store-ring-preview">
                          <ProfileRing ringId={ring.id} size="medium">
                            <div className="demo-avatar-small">
                              <span>👤</span>
                            </div>
                          </ProfileRing>
                        </div>
                        
                        <div className="store-ring-info">
                          <div className="store-ring-name">{ring.name}</div>
                          <div className="store-ring-description">{ring.description}</div>
                          <div className="store-ring-meta">
                            <span className={`ring-set-badge set-${ring.set}`}>
                              {RING_SET_INFO[ring.set]?.icon} {RING_SET_INFO[ring.set]?.name}
                            </span>
                            <span className={`rarity-badge rarity-${ring.rarity}`}>
                              {ring.rarity}
                            </span>
                            <span className="price-badge">
                              🪙 {ring.price}
                            </span>
                          </div>
                          {ring.imageUrl && (
                            <div className="has-custom-image">📸 Custom Image</div>
                          )}
                        </div>
                        
                        <div className="store-ring-actions">
                          <button 
                            className={`toggle-availability-button ${ring.isActive ? 'active' : 'inactive'}`}
                            onClick={() => handleToggleAvailability(ring.id, ring.isActive)}
                          >
                            {ring.isActive ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                        
                        {!ring.isActive && (
                          <div className="ring-inactive-overlay">
                            <span>Disabled</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-store-rings">
                    <div className="no-rings-icon">🛍️</div>
                    <div className="no-rings-text">
                      No custom store rings created yet.
                      <br />
                      Create your first ring to get started!
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Create New Ring Tab */}
            {activeTab === 'create' && (
              <div className="create-store-ring-section">
                <h3>Create New Store Ring</h3>
                
                <div className="create-ring-layout">
                  {/* Form */}
                  <div className="create-ring-form-container">
                    <form onSubmit={handleCreateRing} className="create-store-ring-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Ring Name</label>
                          <input
                            type="text"
                            value={newRing.name}
                            onChange={(e) => setNewRing({...newRing, name: e.target.value})}
                            placeholder="Enter ring name"
                            maxLength={50}
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Collection</label>
                          <select
                            value={newRing.set}
                            onChange={(e) => setNewRing({...newRing, set: e.target.value})}
                          >
                            {Object.entries(RING_SET_INFO).map(([setKey, setInfo]) => (
                              <option key={setKey} value={setKey}>
                                {setInfo.icon} {setInfo.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={newRing.description}
                          onChange={(e) => setNewRing({...newRing, description: e.target.value})}
                          placeholder="Describe this ring"
                          maxLength={200}
                          rows={3}
                          required
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Rarity</label>
                          <select
                            value={newRing.rarity}
                            onChange={(e) => setNewRing({...newRing, rarity: e.target.value})}
                          >
                            {Object.entries(RING_RARITIES).map(([rarityKey, rarityData]) => (
                              <option key={rarityKey} value={rarityKey} disabled={rarityKey === 'special'}>
                                {rarityKey.charAt(0).toUpperCase() + rarityKey.slice(1)} 
                                {rarityData.price && ` (${rarityData.price} coins)`}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="form-group">
                          <label>Custom Price (Optional)</label>
                          <input
                            type="number"
                            value={newRing.price}
                            onChange={(e) => setNewRing({...newRing, price: e.target.value})}
                            placeholder={`Default: ${RING_RARITIES[newRing.rarity]?.price || 0}`}
                            min="1"
                            max="10000"
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>CSS Class (Optional)</label>
                        <input
                          type="text"
                          value={newRing.cssClass}
                          onChange={(e) => setNewRing({...newRing, cssClass: e.target.value})}
                          placeholder="custom-ring-class"
                          maxLength={50}
                        />
                        <div className="form-help">
                          Leave empty to auto-generate. Use for custom CSS styling.
                        </div>
                      </div>
                      
                      {/* Visual Designer */}
                      <div className="ring-visual-designer">
                        <h4>Visual Design</h4>
                        
                        <div className="designer-tabs">
                          <button 
                            type="button"
                            className={`designer-tab ${previewMode === 'css' ? 'active' : ''}`}
                            onClick={() => setPreviewMode('css')}
                          >
                            CSS Designer
                          </button>
                          <button 
                            type="button"
                            className={`designer-tab ${previewMode === 'image' ? 'active' : ''}`}
                            onClick={() => setPreviewMode('image')}
                          >
                            Image Upload
                          </button>
                        </div>
                        
                        {previewMode === 'css' && (
                          <div className="css-designer">
                            <div className="form-row">
                              <div className="form-group">
                                <label>Border Style</label>
                                <select
                                  value={newRing.borderStyle}
                                  onChange={(e) => setNewRing({...newRing, borderStyle: e.target.value})}
                                >
                                  <option value="solid">Solid</option>
                                  <option value="dashed">Dashed</option>
                                  <option value="dotted">Dotted</option>
                                  <option value="double">Double</option>
                                </select>
                              </div>
                              
                              <div className="form-group">
                                <label>Border Width (px)</label>
                                <input
                                  type="range"
                                  min="1"
                                  max="10"
                                  value={newRing.borderWidth}
                                  onChange={(e) => setNewRing({...newRing, borderWidth: e.target.value})}
                                />
                                <span>{newRing.borderWidth}px</span>
                              </div>
                            </div>
                            
                            <div className="form-group">
                              <label>Border Color</label>
                              <input
                                type="color"
                                value={newRing.borderColor}
                                onChange={(e) => setNewRing({...newRing, borderColor: e.target.value})}
                              />
                            </div>
                            
                            <div className="form-row">
                              <div className="form-group">
                                <label>Animation</label>
                                <select
                                  value={newRing.animationType}
                                  onChange={(e) => setNewRing({...newRing, animationType: e.target.value})}
                                >
                                  <option value="none">None</option>
                                  <option value="pulse">Pulse</option>
                                  <option value="rotate">Rotate</option>
                                  <option value="glow">Glow</option>
                                </select>
                              </div>
                              
                              {newRing.animationType === 'glow' && (
                                <div className="form-group">
                                  <label>Glow Intensity</label>
                                  <input
                                    type="range"
                                    min="0"
                                    max="30"
                                    value={newRing.glowIntensity}
                                    onChange={(e) => setNewRing({...newRing, glowIntensity: e.target.value})}
                                  />
                                  <span>{newRing.glowIntensity}px</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {previewMode === 'image' && (
                          <div className="image-uploader">
                            <div className="form-group">
                              <label>Upload Ring Image</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                              />
                              <div className="form-help">
                                Upload a custom image for the ring border/effect. Max 2MB.
                              </div>
                            </div>
                            
                            <div className="form-group">
                              <label>Or Image URL</label>
                              <input
                                type="url"
                                value={newRing.imageUrl}
                                onChange={(e) => setNewRing({...newRing, imageUrl: e.target.value})}
                                placeholder="https://example.com/ring-image.png"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <button 
                        type="submit" 
                        className="create-store-ring-button"
                        disabled={creating}
                      >
                        {creating ? 'Creating...' : 'Create Ring'}
                      </button>
                    </form>
                  </div>
                  
                  {/* Preview */}
                  <div className="ring-preview-container">
                    <h4>Preview</h4>
                    <div className="ring-preview-display">
                      <style>{generatePreviewCSS()}</style>
                      <div className={`profile-ring-container profile-ring-medium ${newRing.cssClass || 'ring-preview'}`}>
                        <div className="profile-ring-inner">
                          <div className="demo-avatar">
                            <span>👤</span>
                          </div>
                        </div>
                        <div className="profile-ring-border"></div>
                      </div>
                    </div>
                    
                    <div className="preview-info">
                      <div className="preview-name">{newRing.name || 'Ring Name'}</div>
                      <div className="preview-set">
                        {RING_SET_INFO[newRing.set]?.icon} {RING_SET_INFO[newRing.set]?.name}
                      </div>
                      <div className="preview-price">
                        🪙 {newRing.price || RING_RARITIES[newRing.rarity]?.price || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminStoreRingManager;